import { useEffect, useRef, useMemo, memo, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { addBusinessDays } from 'date-fns';
import { format, parseISO, eachDayOfInterval } from 'date-fns';
import { Spinner } from '@/components/ui/Spinner';
import { Modal } from '@/components/ui/Modal';
import { StatusDropdown } from '@/components/features/tasks/StatusDropdown';
import { useUpdateTask, useUpdateTaskStatus } from '@/hooks/useTasks';
import { useSprints } from '@/hooks/useSprints';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { ZoomControls } from './ZoomControls';
import { SprintBoundaries } from './SprintBoundaries';
import { TaskDateModal } from './TaskDateModal';
import { ZoomLevel, ZOOM_LEVEL_DAY_WIDTH, ZOOM_LEVEL_ORDER } from '@/types/timeline';
import { formatDateForInput } from '@/utils/dateUtils';
import type { ZoomLevel as SprintZoomLevel } from '@/types/sprint';
import type { TaskWithProject } from '@/hooks/useMemberTasks';
import type { Database } from '@/types/database.types';
import type * as D3 from 'd3';

type TaskStatus = Database['public']['Tables']['tasks']['Row']['status'];
type TaskType = Database['public']['Enums']['task_type'];

interface TimelineTask {
    id: number;
    name: string;
    projectName: string;
    projectId: number;
    start: Date;
    end: Date;
    type: TaskType;
    status: TaskStatus;
    durationDays: number;
}

const getTaskColor = (type: TaskType): string => {
    const colorMap: Record<TaskType, string> = {
        design: '#9333EA',
        development: '#2563EB',
        integration: '#4F46E5',
        code_review: '#0891B2',
        qa: '#059669',
    };
    return colorMap[type] || '#6B7280';
};

const getStatusStyle = (status: TaskStatus): { opacity: number; strikethrough: boolean } => {
    switch (status) {
        case 'completed': return { opacity: 0.5, strikethrough: true };
        case 'not_started': return { opacity: 0.75, strikethrough: false };
        case 'blocked':   return { opacity: 0.85, strikethrough: false };
        default:          return { opacity: 1.0, strikethrough: false };
    }
};

const STATUS_DOT_COLORS: Record<TaskStatus, string> = {
    not_started: '#9CA3AF', // gray-400
    in_progress: '#3B82F6', // blue-500
    completed: '#22C55E',   // green-500
    blocked: '#EF4444',     // red-500
};

interface StatusEditState {
    taskId: number;
    taskName: string;
    currentStatus: TaskStatus;
    projectId: number;
}

const calculateDurationDays = (start: Date, end: Date): number => {
    const diffTime = end.getTime() - start.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const MARGIN = { top: 60, right: 40, bottom: 60, left: 200 } as const;

const formatDateLabel = (d: Date): string =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const getTickConfig = (zoom: ZoomLevel): d3.TimeInterval => {
    switch (zoom) {
        case ZoomLevel.Day:    return d3.timeDay.every(1)!;
        case ZoomLevel.Week:   return d3.timeWeek.every(1)!;
        case ZoomLevel.Sprint: return d3.timeWeek.every(2)!;
        case ZoomLevel.Month:  return d3.timeMonth.every(1)!;
    }
};

const getTickFormat = (zoom: ZoomLevel): ((d: Date) => string) => {
    switch (zoom) {
        case ZoomLevel.Day:    return (d: Date) => format(d, 'MMM d');
        case ZoomLevel.Week:   return (d: Date) => format(d, 'MMM d');
        case ZoomLevel.Sprint: return (d: Date) => format(d, 'MMM d');
        case ZoomLevel.Month:  return (d: Date) => format(d, 'MMM yyyy');
    }
};

const FIRST_DRAG_STORAGE_KEY = 'planarc-member-timeline-first-drag-seen';

interface MemberTimelineProps {
    tasks: TaskWithProject[];
    isLoading: boolean;
    memberId?: number;
}

interface D3RenderState {
    xScale: D3.ScaleTime<number, number>;
    innerHeight: number;
    timelineRange: { start: Date; end: Date };
    zoomLevel: SprintZoomLevel;
}

interface KeyboardEditState {
    taskId: number;
    taskName: string;
    currentStartDate: string;
    currentEndDate: string;
}

/**
 * MemberTimeline - Renders team member's tasks as a horizontal timeline using D3.js.
 * Story 3.2: Adds drag-and-drop task movement with visual feedback, optimistic UI,
 * keyboard alternative, ARIA support, and touch support via d3.drag pointer events.
 */
const MemberTimelineComponent = ({ tasks, isLoading, memberId }: MemberTimelineProps) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const xScaleRef = useRef<d3.ScaleTime<number, number> | null>(null);
    const d3GroupRef = useRef<SVGGElement>(null);
    const isFirstRenderRef = useRef(true);

    const storageKey = memberId != null ? `member-timeline-zoom-${memberId}` : 'member-timeline-zoom';
    const [zoomLevel, setZoomLevel] = useLocalStorage<ZoomLevel>(storageKey, ZoomLevel.Week);

    const [scrollLeft, setScrollLeft] = useState(0);
    const [d3RenderCount, setD3RenderCount] = useState(0);
    const [d3State, setD3State] = useState<D3RenderState | null>(null);

    const sprints = useSprints();
    const updateTaskMutation = useUpdateTask();
    const updateStatusMutation = useUpdateTaskStatus();
    const [statusEditState, setStatusEditState] = useState<StatusEditState | null>(null);
    const setStatusEditStateRef = useRef(setStatusEditState);
    setStatusEditStateRef.current = setStatusEditState;

    const [optimisticUpdates, setOptimisticUpdates] = useState<Map<number, { start_date: string; end_date: string }>>(new Map());
    const [dragAnnouncement, setDragAnnouncement] = useState('');
    const [keyboardEditState, setKeyboardEditState] = useState<KeyboardEditState | null>(null);
    const [showDragHint, setShowDragHint] = useState(false);

    const handleDragEndRef = useRef<(taskId: number, projectId: number, daysDelta: number) => void>(() => {});
    const setKeyboardEditStateRef = useRef(setKeyboardEditState);
    setKeyboardEditStateRef.current = setKeyboardEditState;

    const handleDragEnd = useCallback(async (taskId: number, _projectId: number, daysDelta: number) => {
        if (daysDelta === 0) return;

        const task = tasks.find(t => t.id === taskId);
        if (!task) return;

        const origStart = parseISO(task.start_date);
        const origEnd = parseISO(task.end_date);

        const newStart = addBusinessDays(origStart, daysDelta);
        const newEnd = addBusinessDays(origEnd, daysDelta);

        const newStartStr = formatDateForInput(newStart);
        const newEndStr = formatDateForInput(newEnd);

        setOptimisticUpdates(prev => new Map(prev).set(taskId, { start_date: newStartStr, end_date: newEndStr }));
        setDragAnnouncement(`Task moved to ${newStartStr} – ${newEndStr}`);

        try {
            await updateTaskMutation.mutateAsync({ id: taskId, updates: { start_date: newStartStr, end_date: newEndStr } });
        } catch {
            setOptimisticUpdates(prev => {
                const next = new Map(prev);
                next.delete(taskId);
                return next;
            });
            setDragAnnouncement('Failed to save task move. Changes reverted.');
        } finally {
            setOptimisticUpdates(prev => {
                const next = new Map(prev);
                next.delete(taskId);
                return next;
            });
        }
    }, [tasks, updateTaskMutation]);

    handleDragEndRef.current = handleDragEnd;

    const timelineData = useMemo<TimelineTask[] | null>(() => {
        if (!tasks || tasks.length === 0) return null;

        const valid: TimelineTask[] = [];
        for (const task of tasks) {
            if (!task.start_date || !task.end_date) continue;

            const optimistic = optimisticUpdates.get(task.id);
            const startStr = optimistic?.start_date ?? task.start_date;
            const endStr = optimistic?.end_date ?? task.end_date;

            const start = parseISO(startStr);
            const end = parseISO(endStr);
            if (isNaN(start.getTime()) || isNaN(end.getTime())) continue;
            if (start > end) continue;

            valid.push({
                id: task.id,
                name: task.name,
                projectName: task.project?.name || 'Unknown Project',
                projectId: task.project_id,
                start,
                end,
                type: task.type,
                status: task.status,
                durationDays: calculateDurationDays(start, end),
            });
        }
        return valid.length > 0 ? valid : null;
    }, [tasks, optimisticUpdates]);

    const visibleDateRange = useMemo(() => {
        if (!xScaleRef.current || !containerRef.current) return undefined;
        const containerWidth = containerRef.current.clientWidth;
        const startDate = xScaleRef.current.invert(scrollLeft);
        const endDate = xScaleRef.current.invert(scrollLeft + containerWidth - MARGIN.left);
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return undefined;
        return `${formatDateLabel(startDate)} – ${formatDateLabel(endDate)}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollLeft, d3RenderCount]);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setScrollLeft(e.currentTarget.scrollLeft);
    }, []);

    const scrollToToday = useCallback(() => {
        if (!containerRef.current || !xScaleRef.current) return;
        const todayPx = xScaleRef.current(new Date());
        const containerWidth = containerRef.current.clientWidth;
        const targetScroll = todayPx - containerWidth / 2;
        containerRef.current.scrollTo({ left: Math.max(0, targetScroll), behavior: 'smooth' });
    }, []);

    const scrollByUnit = useCallback((direction: 'left' | 'right') => {
        if (!containerRef.current) return;
        containerRef.current.scrollBy({ left: direction === 'right' ? 200 : -200, behavior: 'smooth' });
    }, []);

    const scrollToEdge = useCallback((edge: 'start' | 'end') => {
        if (!containerRef.current) return;
        if (edge === 'start') {
            containerRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
            containerRef.current.scrollTo({ left: containerRef.current.scrollWidth, behavior: 'smooth' });
        }
    }, []);

    const zoomIn = useCallback(() => {
        const idx = ZOOM_LEVEL_ORDER.indexOf(zoomLevel);
        if (idx > 0) setZoomLevel(ZOOM_LEVEL_ORDER[idx - 1]!);
    }, [zoomLevel, setZoomLevel]);

    const zoomOut = useCallback(() => {
        const idx = ZOOM_LEVEL_ORDER.indexOf(zoomLevel);
        if (idx < ZOOM_LEVEL_ORDER.length - 1) setZoomLevel(ZOOM_LEVEL_ORDER[idx + 1]!);
    }, [zoomLevel, setZoomLevel]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!wrapperRef.current?.contains(e.target as Node)) return;
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;
            switch (e.key) {
                case '+': case '=': e.preventDefault(); zoomIn(); break;
                case '-': e.preventDefault(); zoomOut(); break;
                case 't': case 'T': e.preventDefault(); scrollToToday(); break;
                case 'Home': e.preventDefault(); scrollToEdge('start'); break;
                case 'End': e.preventDefault(); scrollToEdge('end'); break;
                case 'ArrowLeft': if (e.ctrlKey || e.metaKey) { e.preventDefault(); scrollByUnit('left'); } break;
                case 'ArrowRight': if (e.ctrlKey || e.metaKey) { e.preventDefault(); scrollByUnit('right'); } break;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [zoomIn, zoomOut, scrollToToday, scrollToEdge, scrollByUnit]);

    useEffect(() => {
        if (!svgRef.current || !containerRef.current || !d3GroupRef.current || !timelineData || timelineData.length === 0) {
            return;
        }

        const svg = d3.select(svgRef.current);
        const wasFirstRender = isFirstRenderRef.current;
        isFirstRenderRef.current = false;
        if (!wasFirstRender) {
            svg.style('transition', 'opacity 0.3s ease').style('opacity', '0');
        }

        const containerWidth = containerRef.current.clientWidth;
        const margin = MARGIN;
        const barHeight = 40;
        const barPadding = 20;
        const innerHeight = timelineData.length * (barHeight + barPadding);
        const totalHeight = innerHeight + margin.top + margin.bottom;

        const allDates = timelineData.flatMap((d) => [d.start, d.end]);
        const minDate = d3.min(allDates) || new Date();
        const maxDate = d3.max(allDates) || new Date();

        const MS_ONE_DAY = 24 * 60 * 60 * 1000;
        const dateRange = maxDate.getTime() - minDate.getTime();
        const paddingMs = Math.max(dateRange * 0.05, MS_ONE_DAY);
        const paddedMinDate = new Date(minDate.getTime() - paddingMs);
        const paddedMaxDate = new Date(maxDate.getTime() + paddingMs);

        const dayWidth = ZOOM_LEVEL_DAY_WIDTH[zoomLevel];
        const timelineDays = Math.ceil((paddedMaxDate.getTime() - paddedMinDate.getTime()) / (1000 * 60 * 60 * 24));
        const calculatedWidth = Math.max(containerWidth, timelineDays * dayWidth);
        const width = calculatedWidth - margin.left - margin.right;

        svg
            .attr('width', calculatedWidth)
            .attr('height', totalHeight)
            .attr('viewBox', `0 0 ${calculatedWidth} ${totalHeight}`)
            .style('display', 'block');

        const g = d3.select(d3GroupRef.current);
        g.attr('transform', `translate(${margin.left},${margin.top})`);
        g.selectAll('*').remove();

        const xScale = d3.scaleTime().domain([paddedMinDate, paddedMaxDate]).range([0, width]);
        xScaleRef.current = xScale;

        const yScale = d3
            .scaleBand()
            .domain(timelineData.map((_, i) => i.toString()))
            .range([0, innerHeight])
            .padding(0.2);

        const tickInterval = getTickConfig(zoomLevel);
        const tickFmt = getTickFormat(zoomLevel);

        // Weekend shading
        if (zoomLevel === ZoomLevel.Day || zoomLevel === ZoomLevel.Week) {
            const saturdays = eachDayOfInterval({ start: paddedMinDate, end: paddedMaxDate })
                .filter((d) => d.getDay() === 6);
            saturdays.forEach((saturday) => {
                const sundayEnd = new Date(saturday.getTime() + 2 * 24 * 60 * 60 * 1000);
                g.append('rect')
                    .attr('x', xScale(saturday))
                    .attr('y', -margin.top / 2)
                    .attr('width', xScale(sundayEnd) - xScale(saturday))
                    .attr('height', totalHeight)
                    .attr('fill', '#F3F4F6')
                    .attr('opacity', 0.5)
                    .attr('pointer-events', 'none');
            });
        }

        // Time axis
        g.append('g')
            .attr('class', 'x-axis')
            .call(d3.axisTop(xScale)
                .ticks(tickInterval)
                .tickFormat(tickFmt as (d: Date | d3.NumberValue) => string))
            .selectAll('text')
            .style('text-anchor', 'middle')
            .style('font-size', '12px')
            .style('fill', '#4B5563');

        // Today indicator
        const today = new Date();
        const [domainStart, domainEnd] = xScale.domain();
        if (domainStart && domainEnd && today >= domainStart && today <= domainEnd) {
            g.append('line')
                .attr('x1', xScale(today))
                .attr('x2', xScale(today))
                .attr('y1', -margin.top / 2)
                .attr('y2', timelineData.length * (barHeight + barPadding))
                .attr('stroke', '#DC2626')
                .attr('stroke-width', 2)
                .attr('stroke-dasharray', '4,4')
                .attr('opacity', 0.8);
        }

        // Drop indicator — shown during drag to indicate exact drop date
        const dropIndicator = g.append('line')
            .attr('class', 'drag-drop-indicator')
            .attr('y1', -margin.top / 2)
            .attr('y2', innerHeight)
            .attr('stroke', '#2563EB')
            .attr('stroke-width', 2)
            .attr('stroke-dasharray', '4,4')
            .attr('pointer-events', 'none')
            .style('display', 'none');

        // Task bar groups
        const bars = g
            .selectAll<SVGGElement, TimelineTask>('.task-bar')
            .data(timelineData)
            .enter()
            .append('g')
            .attr('class', 'task-bar')
            .attr('transform', (_, i) => `translate(0,${yScale(i.toString())})`);

        // Task rectangles — fill reflects status (not_started=gray, completed=gray, else type color)
        bars.append('rect')
            .attr('x', (d) => xScale(d.start))
            .attr('width', (d) => Math.max(5, xScale(d.end) - xScale(d.start)))
            .attr('height', barHeight)
            .attr('fill', (d) => {
                if (d.status === 'not_started') return '#D1D5DB'; // gray-300
                if (d.status === 'completed') return '#9CA3AF';   // gray-400
                return getTaskColor(d.type);
            })
            .attr('opacity', (d) => getStatusStyle(d.status).opacity)
            .attr('rx', 4)
            .attr('stroke', (d) => (d.status === 'blocked' ? '#DC2626' : 'none'))
            .attr('stroke-width', (d) => (d.status === 'blocked' ? 2 : 0))
            .append('title')
            .text((d) =>
                `${d.name}\nProject: ${d.projectName}\nType: ${d.type}\nStatus: ${d.status}\n${format(d.start, 'MMM d, yyyy')} - ${format(d.end, 'MMM d, yyyy')}\nDuration: ${d.durationDays} days`
            );

        // Left border accent
        bars.append('rect')
            .attr('x', (d) => xScale(d.start))
            .attr('width', 4)
            .attr('height', barHeight)
            .attr('fill', (d) => getTaskColor(d.type))
            .attr('rx', 4)
            .style('pointer-events', 'none');

        // Status dot — clickable colored indicator; press S when task is focused for keyboard access
        const statusDots = bars.append('circle')
            .attr('class', 'task-status-dot')
            .attr('cx', (d) => Math.max(xScale(d.start) + 20, xScale(d.end) - 8))
            .attr('cy', barHeight / 2)
            .attr('r', 4.5)
            .attr('fill', (d) => STATUS_DOT_COLORS[d.status])
            .attr('stroke', 'white')
            .attr('stroke-width', 1)
            .attr('cursor', 'pointer')
            .attr('opacity', (d) => {
                const barW = Math.max(5, xScale(d.end) - xScale(d.start));
                return barW >= 28 ? 0.95 : 0;
            })
            .attr('pointer-events', (d) => {
                const barW = Math.max(5, xScale(d.end) - xScale(d.start));
                return barW >= 28 ? 'all' : 'none';
            });

        statusDots.append('title')
            .text((d) => `Status: ${d.status.replace(/_/g, ' ')}. Click to change (or press S when focused).`);

        statusDots
            .on('pointerdown.status', (event: PointerEvent) => event.stopPropagation())
            .on('click.status', (event: MouseEvent, d: TimelineTask) => {
                event.stopPropagation();
                setStatusEditStateRef.current({
                    taskId: d.id,
                    taskName: d.name,
                    currentStatus: d.status,
                    projectId: d.projectId,
                });
            });

        // Bar labels
        bars.append('text')
            .attr('x', (d) => xScale(d.start) + 8)
            .attr('y', barHeight / 2)
            .attr('dy', '0.35em')
            .style('font-size', '12px')
            .style('font-weight', '500')
            .style('fill', '#FFFFFF')
            .style('text-decoration', (d) => (getStatusStyle(d.status).strikethrough ? 'line-through' : 'none'))
            .style('pointer-events', 'none')
            .text((d) => {
                const label = `${d.name} (${d.projectName}) - ${d.durationDays}d`;
                const availableWidth = xScale(d.end) - xScale(d.start) - 16;
                if (availableWidth < 100) return '';
                return label.length > 40 ? label.substring(0, 37) + '...' : label;
            });

        // Row labels
        bars.append('text')
            .attr('x', -10)
            .attr('y', barHeight / 2)
            .attr('dy', '0.35em')
            .style('text-anchor', 'end')
            .style('font-size', '12px')
            .style('fill', '#374151')
            .style('font-weight', '500')
            .text((d) => {
                const maxLength = 25;
                return d.name.length > maxLength ? d.name.substring(0, maxLength - 3) + '...' : d.name;
            });

        // Capacity gap labels
        for (let i = 0; i < timelineData.length - 1; i++) {
            const current = timelineData[i]!;
            const next = timelineData[i + 1]!;
            const gapMs = next.start.getTime() - current.end.getTime();
            const gapDays = Math.floor(gapMs / (1000 * 60 * 60 * 24));
            if (gapDays <= 0) continue;
            const gapMidX = (xScale(current.end) + xScale(next.start)) / 2;
            const rowY = yScale(i.toString()) ?? 0;
            const nextRowY = yScale((i + 1).toString()) ?? rowY + barHeight + barPadding;
            const gapY = rowY + barHeight + (nextRowY - rowY - barHeight) / 2;
            g.append('text')
                .attr('x', gapMidX)
                .attr('y', gapY)
                .attr('dy', '0.35em')
                .style('text-anchor', 'middle')
                .style('font-size', '10px')
                .style('fill', '#9CA3AF')
                .style('pointer-events', 'none')
                .text(`${gapDays}d free`);
        }

        // ── Drag-and-drop behaviour ───────────────────────────────────────────
        bars.each(function(taskData, i) {
            const rowY = yScale(i.toString()) ?? i * (barHeight + barPadding);
            const originalTransform = `translate(0,${rowY})`;
            const group = d3.select<SVGGElement, TimelineTask>(this);

            group
                .attr('tabindex', '0')
                .attr('role', 'button')
                .attr('aria-label',
                    `Task: ${taskData.name}, ${taskData.start.toLocaleDateString()} to ${taskData.end.toLocaleDateString()}. Press Enter to move with keyboard.`)
                .style('cursor', 'grab')
                .style('outline', 'none');

            group.on('keydown.taskdrag', function(event: KeyboardEvent) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setKeyboardEditStateRef.current({
                        taskId: taskData.id,
                        taskName: taskData.name,
                        currentStartDate: formatDateForInput(taskData.start),
                        currentEndDate: formatDateForInput(taskData.end),
                    });
                } else if (event.key === 's' || event.key === 'S') {
                    event.preventDefault();
                    setStatusEditStateRef.current({
                        taskId: taskData.id,
                        taskName: taskData.name,
                        currentStatus: taskData.status,
                        projectId: taskData.projectId,
                    });
                }
            });

            let cumulativeDx = 0;
            let dragActive = false;
            let dragCancelled = false;
            let escapeCleanup = () => {};

            const drag = d3.drag<SVGGElement, TimelineTask>()
                .on('start', function() {
                    cumulativeDx = 0;
                    dragActive = false;
                    dragCancelled = false;

                    const onEscape = (e: KeyboardEvent) => {
                        if (e.key === 'Escape' && dragActive) {
                            e.preventDefault();
                            dragCancelled = true;
                            d3.select(this)
                                .attr('transform', originalTransform)
                                .style('cursor', 'grab')
                                .style('opacity', null)
                                .style('filter', null);
                            dropIndicator.style('display', 'none');
                            setDragAnnouncement('Drag cancelled. Task returned to original position.');
                        }
                    };
                    document.addEventListener('keydown', onEscape);
                    escapeCleanup = () => document.removeEventListener('keydown', onEscape);
                })
                .on('drag', function(event) {
                    if (dragCancelled) return;

                    cumulativeDx += (event as D3.D3DragEvent<SVGGElement, TimelineTask, TimelineTask>).dx;

                    if (!dragActive && Math.abs(cumulativeDx) >= 5) {
                        dragActive = true;
                        d3.select(this).raise();
                        setDragAnnouncement(`Dragging task: ${taskData.name}`);
                        if (!localStorage.getItem(FIRST_DRAG_STORAGE_KEY)) {
                            localStorage.setItem(FIRST_DRAG_STORAGE_KEY, 'true');
                            setShowDragHint(true);
                        }
                    }

                    if (!dragActive) return;

                    d3.select(this)
                        .style('opacity', '0.7')
                        .style('filter', 'drop-shadow(0 4px 8px rgba(0,0,0,0.25))')
                        .style('cursor', 'grabbing')
                        .attr('transform', `translate(${cumulativeDx},${rowY})`);

                    const newStartX = xScale(taskData.start) + cumulativeDx;
                    dropIndicator
                        .attr('x1', newStartX)
                        .attr('x2', newStartX)
                        .style('display', null);
                })
                .on('end', function() {
                    escapeCleanup();

                    if (dragCancelled || !dragActive) {
                        d3.select(this)
                            .attr('transform', originalTransform)
                            .style('cursor', 'grab')
                            .style('opacity', null)
                            .style('filter', null);
                        dropIndicator.style('display', 'none');
                        return;
                    }

                    d3.select(this)
                        .attr('transform', originalTransform)
                        .style('cursor', 'grab')
                        .style('opacity', null)
                        .style('filter', null);
                    dropIndicator.style('display', 'none');

                    const daysDelta = Math.round(cumulativeDx / dayWidth);
                    handleDragEndRef.current(taskData.id, taskData.projectId, daysDelta);
                });

            group.call(drag);
        });
        // ── End drag-and-drop ─────────────────────────────────────────────────

        if (!wasFirstRender) {
            svg.transition().duration(300).style('opacity', '1');
        }

        setD3State({
            xScale,
            innerHeight,
            timelineRange: { start: paddedMinDate, end: paddedMaxDate },
            zoomLevel: zoomLevel.toLowerCase() as SprintZoomLevel,
        });

        setD3RenderCount((c) => c + 1);

        return () => { svg.interrupt(); };
    }, [timelineData, zoomLevel]);

    const handleKeyboardConfirm = useCallback((daysDelta: number) => {
        const state = keyboardEditState;
        if (!state) return;
        const task = tasks.find(t => t.id === state.taskId);
        if (task) {
            handleDragEndRef.current(state.taskId, task.project_id, daysDelta);
        }
    }, [keyboardEditState, tasks]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Spinner size="lg" />
            </div>
        );
    }

    if (!tasks || tasks.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <svg
                    className="w-12 h-12 text-gray-400 mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                </svg>
                <p className="text-gray-600">No tasks assigned to this team member</p>
            </div>
        );
    }

    return (
        <div ref={wrapperRef} className="member-timeline-container bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            {/* ARIA live region (AC11, AC21) */}
            <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                {dragAnnouncement}
            </div>

            {/* First-drag hint (AC19) */}
            {showDragHint && (
                <div
                    role="status"
                    className="mb-4 flex items-center justify-between rounded-md bg-blue-50 border border-blue-200 px-4 py-2 text-sm text-blue-800"
                >
                    <span>💡 Tip: Drag tasks left or right to reschedule them. Press Escape to cancel.</span>
                    <button
                        type="button"
                        aria-label="Dismiss tip"
                        onClick={() => setShowDragHint(false)}
                        className="ml-4 text-blue-600 hover:text-blue-800 font-medium"
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* Keyboard edit modal (AC20) */}
            {keyboardEditState && (
                <TaskDateModal
                    taskName={keyboardEditState.taskName}
                    currentStartDate={keyboardEditState.currentStartDate}
                    currentEndDate={keyboardEditState.currentEndDate}
                    onConfirm={handleKeyboardConfirm}
                    onClose={() => setKeyboardEditState(null)}
                />
            )}

            {statusEditState && (
                <Modal
                    isOpen={true}
                    onClose={() => setStatusEditState(null)}
                    title={`Change Status: ${statusEditState.taskName}`}
                    size="sm"
                >
                    <div className="flex flex-col items-start gap-4 py-2">
                        <p className="text-sm text-gray-600">Select a new status for this task:</p>
                        <StatusDropdown
                            currentStatus={statusEditState.currentStatus}
                            onStatusChange={(newStatus) => {
                                updateStatusMutation.mutate({
                                    taskId: statusEditState.taskId,
                                    status: newStatus,
                                    projectId: statusEditState.projectId,
                                });
                                setStatusEditState(null);
                            }}
                        />
                    </div>
                </Modal>
            )}

            <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">Task Timeline</h3>
            </div>

            <ZoomControls
                currentZoom={zoomLevel}
                onZoomChange={setZoomLevel}
                onTodayClick={scrollToToday}
                visibleDateRange={visibleDateRange}
            />

            <div
                ref={containerRef}
                className="overflow-x-auto"
                style={{ maxHeight: '70vh', scrollBehavior: 'smooth' }}
                onScroll={handleScroll}
                aria-label="Team member task timeline"
            >
                <svg ref={svgRef}>
                    {d3State && (
                        <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
                            <SprintBoundaries
                                xScale={d3State.xScale}
                                height={d3State.innerHeight}
                                sprints={sprints}
                                timelineRange={d3State.timelineRange}
                                zoomLevel={d3State.zoomLevel}
                            />
                        </g>
                    )}
                    <g ref={d3GroupRef} />
                </svg>
            </div>

            <div className="mt-4 text-sm text-gray-600">
                <p>
                    Showing {tasks.length} task{tasks.length !== 1 ? 's' : ''} in chronological order. Scroll
                    horizontally to view the full timeline.
                </p>
            </div>
        </div>
    );
};

export const MemberTimeline = memo(MemberTimelineComponent);
